import { vec3 } from 'gl-matrix';
import { PlaneMm } from '~/domain/geometry/patientGeometry';
import { ImageObject } from '../image/ImageObject';
import { ContourTexture } from './ContourTexture';

// ── Resolving a viewed plane to a texture slice ──────────────────────
//
// One derivation, shared by every caller that has to turn "the plane the
// clinician is looking at" into "the slice this texture would write".
// Snapping through `Grid.sliceCenterOnPlane` means a plane that cuts no
// image slice — a rotated view, a sagittal or coronal main view over a
// transversally acquired series, an oblique acquisition — resolves to no
// slice for a preview exactly as it does for a write, instead of the two
// answering from different derivations.
//
// The result is tagged rather than `number | undefined` because callers
// need the two refusals apart: interpolation reports "not a slice plane"
// and "outside the texture" to the user separately, a clear throws on the
// first and no-ops on the second, and the delete panel collapses both to
// an empty plan.

/**
 * Whether `plane` cuts an image slice of `imgObject`, i.e. whether a
 * single slice can be addressed by it at all. False for a rotated view,
 * for a plane whose normal is not the grid's slice axis (a sagittal or
 * coronal view of a transversally acquired series) and for a cut across an
 * obliquely acquired grid — the same refusal
 * {@link Grid.sliceCenterOnPlane} makes, so a slice-addressed operation
 * can be gated on exactly the condition under which its write refuses.
 */
export function isImageSlicePlane(
    imgObject: ImageObject,
    plane: PlaneMm
): boolean {
    return imgObject.parent.grid.sliceCenterOnPlane(plane) !== undefined;
}

/**
 * The index of the image slice `plane` cuts, in the image's own grid, or
 * `undefined` for a plane that cuts none; outside `[0, sizeZ)` beyond it.
 */
export function resolveImageSlice(
    imgObject: ImageObject,
    plane: PlaneMm
): number | undefined {
    const imageGrid = imgObject.parent.grid;
    const sliceCenterWorld = imageGrid.sliceCenterOnPlane(plane);
    if (sliceCenterWorld === undefined) {
        return undefined;
    }
    return Math.floor(imageGrid.toVoxelSpace(sliceCenterWorld)[2]);
}

/**
 * Why a plane addresses no texture slice. The two are kept apart because
 * they are different refusals: a plane that cuts no image slice can never
 * address a slice of any ROI, while a plane that cuts one the texture does
 * not cover is a no-op for this ROI alone. `outsideTexture` still names the
 * texture slice the plane would address, below 0 or beyond the texture.
 */
export type TextureSliceResolution =
    | { readonly kind: 'slice'; readonly sliceZ: number }
    | { readonly kind: 'notASlicePlane' }
    | { readonly kind: 'outsideTexture'; readonly sliceZ: number };

/**
 * Resolves a viewed plane to the texture slice that holds it: the centre of
 * the image slice the plane cuts ({@link Grid.sliceCenterOnPlane}), floored
 * into `texture`'s own voxel space (see
 * {@link Grid.snapPositionToSliceCenter} for why floor, not round, is
 * correct) and checked against that texture's extent.
 *
 * A caller that cannot act on a resolution must treat it as a no-op, never
 * as slice 0.
 */
export function resolveTextureSlice(
    texture: ContourTexture,
    imgObject: ImageObject,
    plane: PlaneMm
): TextureSliceResolution {
    const sliceCenterWorld = imgObject.parent.grid.sliceCenterOnPlane(plane);
    if (sliceCenterWorld === undefined) {
        return { kind: 'notASlicePlane' };
    }
    const sliceZ = Math.floor(
        texture.textureGrid.toVoxelSpace(sliceCenterWorld)[2]
    );
    if (!texture.textureGrid.includesVoxel(vec3.fromValues(0, 0, sliceZ))) {
        return { kind: 'outsideTexture', sliceZ };
    }
    return { kind: 'slice', sliceZ };
}
